import { Component, forwardRef, useEffect, useId, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { coffreFileUrl, releaseMediaFile, uploadCoffreFile, vaultPathFromUrl } from "./coffre";
import { fr } from "@blocknote/core/locales";
import { MantineProvider } from "@mantine/core";
import { BasicTextStyleButton, BlockTypeSelect, ColorStyleButton, CreateLinkButton, FormattingToolbar, NestBlockButton, TextAlignButton, UnnestBlockButton, getDefaultReactSlashMenuItems, useBlockNoteEditor, useComponentsContext, useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";

const INSERT = new Set(["code_block", "divider", "table", "image", "video", "audio", "file", "emoji"]);

export type PadUpload = { folder: string; ownerId: number; pagePath?: string };

function safeFileName(name: string): string {
  const base = name.replace(/[^\w.\-()+\u00C0-\u024F ]+/gi, "_").trim().slice(0, 80);
  return base || "fichier";
}

async function storePadFile(folder: string, ownerId: number, file: File): Promise<string> {
  const named = new File([file], `page-${Date.now().toString(36)}-${safeFileName(file.name)}`, { type: file.type || "application/octet-stream" });
  const path = await uploadCoffreFile(folder, named, ownerId);
  return `${coffreFileUrl(path, ownerId)}&inline=1`;
}

function PadInserts() {
  const editor = useBlockNoteEditor();
  const Components = useComponentsContext();
  if (!Components) return null;
  const items = getDefaultReactSlashMenuItems(editor).filter((item) => INSERT.has(String(item.key)));
  return (
    <>
      {items.map((item) => (
        <Components.FormattingToolbar.Button
          key={item.key}
          label={item.title}
          icon={item.icon}
          mainTooltip={item.title}
          onClick={() => {
            try {
              editor.focus();
              item.onItemClick();
            } catch {
              /* l’éditeur est déjà fermé */
            }
          }}
        />
      ))}
    </>
  );
}

function splitBlocks(source: string): { markdown: string; blocks: unknown[] | null } {
  const raw = source.replace(/\r\n/g, "\n");
  const wrapped = raw.match(/<!--BLOCKS_JSON\s*\n([\s\S]*?)\n-->\s*$/);
  if (wrapped) {
    const blocks = parseBlocks(wrapped[1]);
    if (blocks) return { markdown: raw.slice(0, wrapped.index).replace(/\s+$/, ""), blocks };
  }
  const marker = "<!--BLOCKS_JSON-->";
  const at = raw.lastIndexOf(marker);
  if (at >= 0) {
    const blocks = parseBlocks(raw.slice(at + marker.length));
    if (blocks) return { markdown: raw.slice(0, at).replace(/\s+$/, ""), blocks };
  }
  return { markdown: raw, blocks: null };
}

function parseBlocks(raw: string): unknown[] | null {
  try {
    const parsed = JSON.parse(raw.trim()) as unknown;
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export function readPad(source: string): { markdown: string; blocks: unknown[] | null } {
  return splitBlocks(source);
}

export function writePad(markdown: string, blocks: unknown[] | null): string {
  return composeBlocks(markdown, blocks, false);
}

function composeBlocks(markdown: string, blocks: unknown[] | null, kanban: boolean): string {
  const text = markdown.replace(/\s+$/, "");
  if (!blocks?.length) return text;
  if (kanban) return `${text}\n\n<!--BLOCKS_JSON-->\n${JSON.stringify(blocks)}`;
  return `${text}\n\n<!--BLOCKS_JSON\n${JSON.stringify(blocks)}\n-->`;
}

class PadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function quietView(tiptap: { editorView: unknown; state: unknown }) {
  const dom = document.createElement("div");
  return {
    dom,
    state: tiptap.state,
    isDestroyed: true,
    composing: false,
    dragging: null,
    editable: false,
    destroy() {},
    focus() {},
    dispatch() {},
    updateState() {},
    setProps() {},
    hasFocus() {
      return false;
    },
    posAtDOM() {
      return 0;
    },
    domAtPos() {
      return { node: dom, offset: 0 };
    },
    coordsAtPos() {
      return { left: 0, right: 0, top: 0, bottom: 0 };
    },
  };
}

function sealEditor(editor: { _tiptapEditor: { editorView: unknown; state: unknown; callbacks?: Record<string, unknown>; unmount: () => void } }) {
  const tiptap = editor._tiptapEditor;
  const quiet = quietView(tiptap);
  Object.defineProperty(tiptap, "view", {
    configurable: true,
    get() {
      return tiptap.editorView ?? quiet;
    },
  });
  const original = tiptap.unmount.bind(tiptap);
  tiptap.unmount = () => {
    if (tiptap.callbacks) {
      delete tiptap.callbacks.selectionUpdate;
      delete tiptap.callbacks.update;
      delete tiptap.callbacks.transaction;
      delete tiptap.callbacks.focus;
      delete tiptap.callbacks.blur;
    }
    try {
      original();
    } catch {
      /* la barre lit encore la sélection pendant que la vue se ferme */
    }
  };
}

type LooseBlock = { id: string; props?: { url?: string }; children?: LooseBlock[] };

function findMedia(blocks: LooseBlock[], wanted: string): LooseBlock | null {
  for (const block of blocks) {
    const url = String(block.props?.url || "");
    if (url && vaultPathFromUrl(url) === wanted) return block;
    const nested = block.children ? findMedia(block.children, wanted) : null;
    if (nested) return nested;
  }
  return null;
}

function mediaUrl(target: EventTarget | null): string {
  if (!(target instanceof Element)) return "";
  const node = target.closest("img, video, audio, a");
  if (node instanceof HTMLImageElement) return node.currentSrc || node.src;
  if (node instanceof HTMLVideoElement || node instanceof HTMLAudioElement) return node.currentSrc || node.src;
  if (node instanceof HTMLAnchorElement) return node.href;
  return "";
}

function serializeBlocks(document: unknown): unknown[] | null {
  try {
    const json = JSON.parse(JSON.stringify(document)) as unknown;
    return Array.isArray(json) && json.length > 0 ? json : null;
  } catch {
    return null;
  }
}

function openingContent(source: string): unknown[] {
  const { markdown, blocks } = splitBlocks(source);
  if (blocks?.length) return blocks;
  const visible = markdown.replace(/\n*<!--BLOCKS_JSON[\s\S]*$/, "").trim();
  if (!visible) return [{ type: "paragraph" }];
  return [{ type: "paragraph", content: visible }];
}

export type NotePadHandle = {
  flush: () => Promise<string>;
};

type NotePadProps = {
  initial: string;
  onChange: (markdown: string) => void;
  onHtml?: (html: string) => void;
  kanban?: boolean;
  portaled?: boolean;
  upload?: PadUpload | null;
  persist?: (markdown: string) => Promise<void>;
};

export const NotePad = forwardRef<NotePadHandle, NotePadProps>(function NotePad({ initial, onChange, onHtml, kanban = false, portaled = false, upload = null, persist }, ref) {
  const domId = `crm-bn-${useId().replace(/:/g, "")}`;
  const onChangeRef = useRef(onChange);
  const onHtmlRef = useRef(onHtml);
  const uploadSpot = useRef(upload);
  const persistRef = useRef(persist);
  const skip = useRef(true);
  const initialRef = useRef(initial);
  const latest = useRef(initial);
  const kanbanRef = useRef(kanban);
  const storedBlocks = useRef(splitBlocks(initial).blocks);
  const [mediaMenu, setMediaMenu] = useState<{ x: number; y: number; url: string } | null>(null);
  const [mediaNote, setMediaNote] = useState("");
  onChangeRef.current = onChange;
  onHtmlRef.current = onHtml;
  uploadSpot.current = upload;
  persistRef.current = persist;
  const editor = useCreateBlockNote({
    dictionary: fr,
    initialContent: openingContent(initial) as never,
    ...(upload
      ? {
          uploadFile: async (file: File) => {
            const spot = uploadSpot.current;
            if (!spot) throw new Error("Téléversement indisponible.");
            return storePadFile(spot.folder, spot.ownerId, file);
          },
        }
      : {}),
  });

  useEffect(() => {
    sealEditor(editor);
  }, [editor]);

  function emitHtml() {
    const publish = onHtmlRef.current;
    if (!publish) return;
    try {
      publish(editor.blocksToHTMLLossy(editor.document));
    } catch {
      /* la vue est déjà démontée */
    }
  }
  const dark = document.documentElement.classList.contains("dark");

  useEffect(() => {
    skip.current = true;
    if (!storedBlocks.current?.length) {
      const visible = initialRef.current.replace(/\n*<!--BLOCKS_JSON[\s\S]*$/, "").trim();
      if (visible) {
        try {
          const parsed = editor.tryParseMarkdownToBlocks(visible);
          if (parsed?.length) editor.replaceBlocks(editor.document, parsed);
        } catch {
          /* le texte brut reste affiché */
        }
      }
    }
    skip.current = false;
    emitHtml();
  }, [editor]);

  function snapshot(): string {
    try {
      const markdown = editor.blocksToMarkdownLossy(editor.document);
      const value = composeBlocks(markdown, serializeBlocks(editor.document), kanbanRef.current);
      latest.current = value;
      return value;
    } catch {
      return latest.current;
    }
  }

  useImperativeHandle(ref, () => ({
    flush() {
      // Le clavier mobile garde la fin de la phrase tant que le champ a le focus.
      return new Promise((resolve) => {
        let timer = 0;
        let cap = 0;
        let stopped = false;
        const finish = () => {
          if (stopped) return;
          stopped = true;
          window.clearTimeout(timer);
          window.clearTimeout(cap);
          queueMicrotask(() => {
            if (typeof unsubscribe === "function") unsubscribe();
          });
          resolve(snapshot());
        };
        const arm = (delay: number) => {
          if (stopped) return;
          window.clearTimeout(timer);
          timer = window.setTimeout(finish, delay);
        };
        const unsubscribe = editor.onChange(() => {
          if (skip.current) return;
          arm(60);
        });
        const root = document.getElementById(domId);
        const active = document.activeElement;
        if (active instanceof HTMLElement && root?.contains(active)) active.blur();
        arm(120);
        cap = window.setTimeout(finish, 400);
      });
    },
  }), [editor, domId]);

  useEffect(() => {
    const unsubscribe = editor.onChange(() => {
      if (skip.current) return;
      try {
        onChangeRef.current(snapshot());
        emitHtml();
      } catch {
        /* la vue est déjà démontée */
      }
    });
    return typeof unsubscribe === "function" ? unsubscribe : undefined;
  }, [editor]);

  useEffect(() => {
    if (!mediaMenu) return;
    const close = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest("[data-media-menu]")) return;
      setMediaMenu(null);
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [mediaMenu]);

  useEffect(() => {
    const root = document.getElementById(domId);
    if (!root?.closest(".crm-phone-task-note")) return;
    let armed = false;
    let startX = 0;
    let startY = 0;
    let startTop = 0;
    const start = (event: TouchEvent) => {
      armed = false;
      if (event.touches.length !== 1) return;
      const target = event.target;
      if (target instanceof Element && target.closest(".crm-pad-bar, button, a, input, select, textarea")) return;
      armed = true;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      startTop = root.scrollTop;
    };
    const move = (event: TouchEvent) => {
      if (!armed || event.touches.length !== 1) return;
      const dx = event.touches[0].clientX - startX;
      const dy = startY - event.touches[0].clientY;
      if (Math.abs(dy) < 8 || Math.abs(dx) > Math.abs(dy)) return;
      const max = root.scrollHeight - root.clientHeight;
      if (max <= 1) return;
      root.scrollTop = Math.min(max, Math.max(0, startTop + dy));
      event.preventDefault();
    };
    root.addEventListener("touchstart", start, { passive: true });
    root.addEventListener("touchmove", move, { passive: false, capture: true });
    return () => {
      root.removeEventListener("touchstart", start);
      root.removeEventListener("touchmove", move, true);
    };
  }, [domId]);

  useEffect(() => {
    const root = document.getElementById(domId);
    if (!root) return;
    const commit = () => {
      if (skip.current) return;
      onChangeRef.current(snapshot());
    };
    const menu = (event: MouseEvent) => {
      const url = mediaUrl(event.target);
      if (!url || !vaultPathFromUrl(url) || (uploadSpot.current?.ownerId ?? 0) > 0) return;
      event.preventDefault();
      setMediaNote("");
      setMediaMenu({ x: event.clientX, y: event.clientY, url });
    };
    root.addEventListener("compositionend", commit);
    root.addEventListener("contextmenu", menu);
    return () => {
      root.removeEventListener("compositionend", commit);
      root.removeEventListener("contextmenu", menu);
    };
  }, [editor, domId]);

  async function removeMedia(url: string) {
    setMediaMenu(null);
    setMediaNote("");
    const path = vaultPathFromUrl(url);
    const spot = uploadSpot.current;
    const blocks = editor.document as LooseBlock[];
    const block = path ? findMedia(blocks, path) : null;
    if (!block) {
      setMediaNote("Suppression impossible.");
      return;
    }
    editor.removeBlocks([block as never]);
    const next = snapshot();
    onChangeRef.current(next);
    if (!path || !spot) return;
    try {
      if (persistRef.current) await persistRef.current(next);
    } catch (reason: unknown) {
      setMediaNote(reason instanceof Error ? reason.message : "Enregistrement impossible.");
      return;
    }
    try {
      const gone = await releaseMediaFile(path, spot.folder, spot.ownerId, spot.pagePath || "");
      if (!gone) setMediaNote("La photo est retirée de cette page. Le fichier reste, une autre page l’utilise encore.");
    } catch (reason: unknown) {
      setMediaNote(reason instanceof Error ? reason.message : "Suppression du fichier impossible.");
    }
  }

  return (
    <div id={domId} className="crm-notepad" data-bn-theme={dark ? "dark" : "light"}>
      {mediaNote ? <p className="mb-2 text-xs text-slate-500">{mediaNote}</p> : null}
      {mediaMenu ? createPortal(
        <div data-media-menu className="fixed z-[90] min-w-36 rounded-xl border border-slate-200 bg-white p-1 text-sm shadow-xl" style={{ left: Math.min(mediaMenu.x, window.innerWidth - 160), top: Math.min(mediaMenu.y, window.innerHeight - 48) }}>
          <button type="button" className="block w-full rounded-lg px-3 py-1.5 text-left font-semibold text-rose-700 hover:bg-rose-50" onClick={() => void removeMedia(mediaMenu.url)}>Supprimer</button>
        </div>,
        document.body,
      ) : null}
      <MantineProvider cssVariablesSelector={`#${domId}`} getRootElement={portaled ? undefined : () => document.getElementById(domId) ?? undefined} forceColorScheme={dark ? "dark" : "light"}>
        <PadBoundary>
        <BlockNoteView editor={editor} theme={dark ? "dark" : "light"} formattingToolbar={false}>
          <div className="crm-pad-bar">
            <FormattingToolbar>
              <BlockTypeSelect />
              <BasicTextStyleButton basicTextStyle="bold" />
              <BasicTextStyleButton basicTextStyle="italic" />
              <BasicTextStyleButton basicTextStyle="underline" />
              <BasicTextStyleButton basicTextStyle="strike" />
              <BasicTextStyleButton basicTextStyle="code" />
              <TextAlignButton textAlignment="left" />
              <TextAlignButton textAlignment="center" />
              <TextAlignButton textAlignment="right" />
              <ColorStyleButton />
              <CreateLinkButton />
              <NestBlockButton />
              <UnnestBlockButton />
              <PadInserts />
            </FormattingToolbar>
          </div>
        </BlockNoteView>
        </PadBoundary>
      </MantineProvider>
    </div>
  );
});
